/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import { Dropdown, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { panelIconButtonStyle } from './panelIconButtonStyle'

interface PanelMenuTriggerProps {
  /** The button's accessible name; also the tooltip unless `tooltip` is set. */
  label: string
  icon: ReactNode
  disabled: boolean
  menu: MenuProps
  /** Overrides the tooltip — e.g. the reason a disabled trigger is disabled. */
  tooltip?: ReactNode
}

/**
 * An icon button in the panel header that opens a menu — the Import / Export
 * triggers for both the organization (root competition) and a team's roster.
 *
 * The tooltip wraps a span rather than the button, so it still shows while the
 * button is disabled — a disabled button fires no pointer events of its own. It
 * sits above the button because the menu opens below it.
 */
const PanelMenuTrigger = ({
  label,
  icon,
  disabled,
  menu,
  tooltip,
}: PanelMenuTriggerProps) => {
  'use memo'

  return (
    <Tooltip title={tooltip ?? label} placement='top'>
      <span css={wrapperStyle}>
        <Dropdown
          trigger={['click']}
          placement='bottomRight'
          disabled={disabled}
          menu={menu}
        >
          <button
            type='button'
            css={panelIconButtonStyle}
            disabled={disabled}
            aria-label={label}
            aria-haspopup='menu'
          >
            {icon}
          </button>
        </Dropdown>
      </span>
    </Tooltip>
  )
}

const wrapperStyle = css`
  display: inline-flex;
`

export default PanelMenuTrigger
